import {AfterViewChecked, Component, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {MatOption} from '@angular/material/core';
import {LdapUser} from '@Services/ldap/ldap/ldap-user.model';
import {IndividuService, PubSubService} from '@Common/core/services';
import {SubSink} from '@Common/core/utils/subsink';
import {ResponseAvisService} from '../../../../common/services/avis/response-avis.service';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {ResponseAvisRequest, TypeAvisEnum} from '../../../../common/services/avis/response-avis.model';
import {Observable} from 'rxjs';
import {ResponseForm} from 'app/demat/common/components/form/models/responsedemat.model';
import {concatMap, finalize} from 'rxjs/operators';
import {ResponseStateInstruction} from '../../../../common/components/form/models';
import {FormListRequestersService} from '../../../../common/services/requesters-form/requesters-form.service';
import {FormListRequesters} from '../../../../common/services/requesters-form/requesters-form.model';
import {GroupListsRequesters} from './group-list-requesters.model';
import {OpinionRequesterCopyRecipients} from './opinion-requester.model';

@Component({
  selector: 'app-request-opinion',
  templateUrl: './request-opinion.component.html',
  styleUrls: ['./request-opinion.component.scss']
})
export class RequestOpinionComponent implements OnInit, AfterViewChecked {
  @Input() response: ResponseForm;
  @Input() lastInstructionState: ResponseStateInstruction;
  @ViewChildren(MatOption) matOptions: QueryList<MatOption>;

  listGuests: OpinionRequesterCopyRecipients[] = [];
  INTRODUCTION = '';
  urlIndividuGetOrCreate = '/demat/individu/';
  responseInstruction = ResponseStateInstruction;
  groupListsSelect: GroupListsRequesters[] = [];
  colsGrid = 1;
  allReadyExist = '';
  subSink = new SubSink();

  private _lastSyncedGuestUids = '';
  private _previouslySelected: OpinionRequesterCopyRecipients[] = [];

  constructor(private individuSvc: IndividuService,
              private pubSubSvc: PubSubService,
              private snackSvc: SnackBarToastyService,
              private avisSvc: ResponseAvisService,
              private formListRequestersSvc: FormListRequestersService) { }

  ngOnInit(): void {
    this._initOpinions();
    this._fetchListOfRequestersForForm(this.response?.form_obj?.id);
  }

  ngAfterViewChecked(): void {
    // Keep MatOption's native selected state in sync with listGuests,
    // without re-running select()/deselect() on every CD cycle unnecessarily.
    const _currentUids = this.listGuests.map(_g => _g.uid).sort().join(',');
    if (_currentUids !== this._lastSyncedGuestUids && this.matOptions) {
      this._lastSyncedGuestUids = _currentUids;
      this._syncOptionSelection();
    }
  }

  private _syncOptionSelection() {
    this.matOptions.forEach(_option => {
      const _value = _option.value as OpinionRequesterCopyRecipients;
      if (!_value) {
        return;
      }
      const _shouldBeSelected = this.listGuests.some(_guest => _guest.uid === _value.uid);
      if (_shouldBeSelected && !_option.selected) {
        _option.select();
      } else if (!_shouldBeSelected && _option.selected) {
        _option.deselect();
      }
    });
  }

  private _initOpinions() {
    this.listGuests = [];
    this.INTRODUCTION = 'Bonjour, merci de me donner un avis sur cette demande.';
  }
  private _fetchListOfRequestersForForm(id: number) {
    if (id) {
      const _extraParams = new Map<string, string>([['forms_demat', id.toString()]]);
      this.subSink.sink = this.formListRequestersSvc
        .listAllItems(_extraParams)
        .subscribe(_data => {
          if (_data.list.length > 0) {
            this._transformListRequestersForSelect(_data.list);
            this.colsGrid = 3;
          }
        });
    }
  }
  private _transformListRequestersForSelect(listRequesters: FormListRequesters[]) {
    this.groupListsSelect = listRequesters.map(_item => ({
      listName: _item.name,
      requesters: _item.requesters_obj.map(_requester => ({
        uid: _requester.user_obj.username,
        displayName: `${_requester.user_obj.first_name} ${_requester.user_obj.last_name}`,
        email: _requester.user_obj.email,
        informations: _requester.informations || ''
      }))
    } as GroupListsRequesters));
  }
  private _sendOpinions(_listGuests: OpinionRequesterCopyRecipients[]): Observable<any> {
    return new Observable<number>(_observer => {
      let _cnt = 0;
      _listGuests.forEach(_guest => {
        const _avis: ResponseAvisRequest = {
          message: this.INTRODUCTION,
          response: this.response.id,
          type_avis: TypeAvisEnum.extern,
          expert_individu: _guest.uid,
          motivation: '',
          avis: null,
        };
        this.subSink.sink = this.avisSvc
          .requestOrRemindOpinion(_avis)
          .subscribe(_ => {
            _cnt++;
            if (_cnt === _listGuests.length) {
              _observer.next(_cnt);
              _observer.complete();
            }
          });
      });
    });
  }
  sendOpinion() {
    this.pubSubSvc.publish('loading', true);
    this._sendOpinions(this.listGuests)
      .pipe(finalize(() => this.pubSubSvc.publish('loading', false)))
      .subscribe(_cnt => {
        const _plural = _cnt > 1;
        const _message = `${_plural ? 'les' : 'la'} demande${_plural ? 's ont bien été envoyées' : ' a bien été envoyée'} `;
        this.snackSvc.toasty('Demande d\'avis', _message, typeToast.success);
        this._initOpinions();
      });
  }
  isRequestOpinionExist(individuUid: string) {
    return new Observable<boolean>(_observer => {
      const params: Map<string, string> = new Map<string, string>([
        ['expert_individu', individuUid],
        ['response', this.response.id.toString()]
      ]);
      this.subSink.sink = this.avisSvc.listAllItems(params).subscribe(_lavis => {
        if (_lavis.list.length > 0) {
          _observer.next(true);
        } else {
          _observer.next(false);
        }
        _observer.complete();
      });
    });
  }
  onSelectIndividuLdap(ldapUser: LdapUser) {
    const uid = ldapUser.uid[0];
    this.subSink.sink = this.individuSvc
      .getOrCreate(uid, this.urlIndividuGetOrCreate)
      .pipe(concatMap(_ind => this.isRequestOpinionExist(_ind.uid)))
      .subscribe(_exist => this._addToOpinionRequesterChips(uid, ldapUser.cn[0], ldapUser.mail[0], _exist));
  }
  onSelectListRequesters(selected: OpinionRequesterCopyRecipients | OpinionRequesterCopyRecipients[]) {
    const _users = Array.isArray(selected) ? selected : [selected];

    if (Array.isArray(selected)) {
      // multiple mode: diff against last known selection to find unchecked items
      const _removedUsers = this._previouslySelected.filter(
        _prev => !_users.some(_cur => _cur.uid === _prev.uid)
      );
      _removedUsers.forEach(_removed => this.deleteGuestChip(_removed));

      const _addedUsers = _users.filter(
        _cur => !this._previouslySelected.some(_prev => _prev.uid === _cur.uid)
      );
      _addedUsers.forEach(_user => {
        this.subSink.sink = this.isRequestOpinionExist(_user.uid)
          .subscribe(_exist => this._addToOpinionRequesterChips(_user.uid, _user.displayName, _user.email, _exist));
      });

      this._previouslySelected = _users;
    } else {
      // single-person call, e.g. from toggleGroupSelection
      this.subSink.sink = this.isRequestOpinionExist(selected.uid)
        .subscribe(_exist => this._addToOpinionRequesterChips(selected.uid, selected.displayName, selected.email, _exist));
    }
  }
  toggleGroupSelection(group: GroupListsRequesters, event: MouseEvent) {
    const _target = event.target as HTMLElement;
    if (_target.closest('mat-option')) {
      return;
    }
    event.stopPropagation();
    group.requesters.forEach(_requester => this.onSelectListRequesters(_requester));
  }
  deleteGuestChip(user: OpinionRequesterCopyRecipients) {
    const _idx = this.listGuests.findIndex(_guest => _guest.uid === user.uid);
    if (_idx > -1) {
      this.listGuests.splice(_idx, 1);
    }
  }
  private _addToOpinionRequesterChips(uid: string, displayName: string, email: string, exist: boolean) {
    this.allReadyExist = '';
    if (! exist && ! this.listGuests.find(_user => _user.uid === uid)) {
      this.listGuests.push(<OpinionRequesterCopyRecipients> {
        uid: uid,
        displayName: displayName,
        email: email
      });
    } else {
      this.allReadyExist = `La personne sollicitée ${displayName} a déjà ajoutée dans la liste ou a été déjà sollicitée (voir onglet "Voir les avis" pour la relancer le cas échéant)`;
      this.snackSvc.toasty('Demande d\'Avis', this.allReadyExist, typeToast.warning, 4000);
    }
  }
}
